import React from 'react';

import { SelectOptGroupType, SelectOptionType } from './types';

export function isOptGroup<T extends SelectOptionType>(
  option: SelectOptionType | SelectOptGroupType<T>,
): option is SelectOptGroupType<T> {
  return 'options' in option;
}

export function flattenOptions<T extends SelectOptionType>(options: T[]) {
  if (!options.some((option) => isOptGroup(option))) return options;

  const flattenList: T[] = [];

  function dig(list: T[], groupName?: string, groupKey?: React.Key) {
    list.forEach((item) => {
      if (!isOptGroup<T>(item)) {
        // Option
        flattenList.push({
          ...item,
          _groupName: groupName,
          _groupKey: groupKey,
        });
      } else {
        // Option Group
        dig(item.options, item.label, item.key);
      }
    });
  }

  dig(options);

  return flattenList;
}

export function groupOptions<T extends SelectOptionType>(options: T[]) {
  if (!options.some((option) => '_groupKey' in option)) return options;

  const groupedList: SelectOptGroupType<T>[] = [];

  function dig(list: T[]) {
    list.forEach((item) => {
      const value = groupedList.find((v) => v.key === item._groupKey);

      if (!value) {
        // Option
        groupedList.push({
          key: item._groupKey,
          label: item._groupName,
          options: [item],
        });
      } else {
        // Option Group
        value.options.push(item);
      }
    });
  }

  dig(options);

  return groupedList;
}

export function mergeOptions<T extends SelectOptionType>(options: T[], selectedOptions?: T[]) {
  if (!selectedOptions) return options;

  const valueMap = new Map<String, T>();

  options.concat(selectedOptions).forEach((option) => valueMap.set(option.value, option));

  return Array.from(valueMap.values());
}
